import {
  getGetApiWebCalendarGetProfilesQueryKey,
  getGetUpdateActivityCalendarProfileQueryKey,
  getGetUpdatePersonalCalendarProfileQueryKey,
  useCreatePersonalWebCalendarProfile,
  useCreateActivityWebCalendarProfile,
  useCreateSharedActivityWebCalendarProfile,
  useCreateSharedPersonalWebCalendarProfile,
  useDeletePersonalWebCalendarProfile,
  useGetDefaultSuspense,
  useGetUpdateActivityCalendarProfileSuspense,
  useGetUpdatePersonalCalendarProfileSuspense,
  useUpdateActivityWebCalendarProfile,
  useUpdatePersonalWebCalendarProfile,
  useUpdateSharedActivityWebCalendarProfile,
  useUpdateSharedPersonalWebCalendarProfile,
  WebCalendarProfileDto,
} from "@/orval/gen"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { DefaultModal } from "@components/default-modal"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import {
  CalendarProfileForm,
  CalendarProfileFormValues,
  defaultCalendarProfileValues,
} from "./CalendarProfileForm"

interface ContentProps {
  isShared: boolean
  onClose: () => void
}

function CreateContent({
  isShared,
  onClose,
  initialValues = defaultCalendarProfileValues,
}: ContentProps & { initialValues?: CalendarProfileFormValues }) {
  const client = useQueryClient()
  const personal = useCreatePersonalWebCalendarProfile()
  const activity = useCreateActivityWebCalendarProfile()
  const sharedPersonal = useCreateSharedPersonalWebCalendarProfile()
  const sharedActivity = useCreateSharedActivityWebCalendarProfile()

  const submit = async (values: CalendarProfileFormValues) => {
    const common = { name: values.name, description: values.description }
    try {
      if (values.provider === "activities") {
        const data = { ...common, settings: values.activitySettings }
        if (isShared) {
          await sharedActivity.mutateAsync({
            data: { ...data, visibleForUserIds: values.visibleForUserIds },
          })
        } else {
          await activity.mutateAsync({ data })
        }
      } else {
        const data = { ...common, settings: values.personalSettings }
        if (isShared) {
          await sharedPersonal.mutateAsync({
            data: { ...data, visibleForUserIds: values.visibleForUserIds },
          })
        } else {
          await personal.mutateAsync({ data })
        }
      }
      await client.invalidateQueries({
        queryKey: getGetApiWebCalendarGetProfilesQueryKey(),
      })
      onClose()
    } catch (error) {
      showErrorNotification(error)
    }
  }

  return (
    <CalendarProfileForm
      initialValues={initialValues}
      isShared={isShared}
      onClose={onClose}
      onSubmit={submit}
    />
  )
}

function CreatePersonalContent(props: ContentProps) {
  const { data: defaults } = useGetDefaultSuspense()
  return (
    <CreateContent
      {...props}
      initialValues={{
        ...defaultCalendarProfileValues,
        name: defaults.name,
        description: defaults.description ?? "",
        personalSettings: defaults.settings,
      }}
    />
  )
}

function EditContent({
  profile,
  initialValues,
  onClose,
}: {
  profile: WebCalendarProfileDto
  initialValues: CalendarProfileFormValues
  onClose: () => void
}) {
  const client = useQueryClient()
  const personal = useUpdatePersonalWebCalendarProfile()
  const activity = useUpdateActivityWebCalendarProfile()
  const sharedPersonal = useUpdateSharedPersonalWebCalendarProfile()
  const sharedActivity = useUpdateSharedActivityWebCalendarProfile()
  const deleteProfile = useDeletePersonalWebCalendarProfile({
    mutation: {
      onSuccess: async () => {
        await client.invalidateQueries({
          queryKey: getGetApiWebCalendarGetProfilesQueryKey(),
        })
        onClose()
      },
      onError: showErrorNotification,
    },
  })

  const submit = async (values: CalendarProfileFormValues) => {
    const common = {
      webCalendarProfileId: profile.webCalendarProfileId!,
      name: values.name,
      description: values.description,
    }
    try {
      if (profile.isActivityCalendar) {
        const data = { ...common, settings: values.activitySettings }
        if (profile.isShared) {
          await sharedActivity.mutateAsync({
            data: { ...data, visibleForUserIds: values.visibleForUserIds },
          })
        } else {
          await activity.mutateAsync({ data })
        }
      } else {
        const data = { ...common, settings: values.personalSettings }
        if (profile.isShared) {
          await sharedPersonal.mutateAsync({
            data: { ...data, visibleForUserIds: values.visibleForUserIds },
          })
        } else {
          await personal.mutateAsync({ data })
        }
      }
      await Promise.all([
        client.invalidateQueries({
          queryKey: getGetApiWebCalendarGetProfilesQueryKey(),
        }),
        client.invalidateQueries({
          queryKey: profile.isActivityCalendar
            ? getGetUpdateActivityCalendarProfileQueryKey({
                profileId: profile.webCalendarProfileId!,
              })
            : getGetUpdatePersonalCalendarProfileQueryKey({
                profileId: profile.webCalendarProfileId!,
              }),
        }),
      ])
      onClose()
    } catch (error) {
      showErrorNotification(error)
    }
  }

  return (
    <CalendarProfileForm
      initialValues={initialValues}
      isShared={!!profile.isShared}
      isEditing
      onDelete={
        profile.allowEditAndDelete
          ? () =>
              deleteProfile.mutate({
                data: profile.webCalendarProfileId!,
              })
          : undefined
      }
      isDeleting={deleteProfile.isPending}
      onClose={onClose}
      onSubmit={submit}
    />
  )
}

function EditPersonalContent(props: {
  profile: WebCalendarProfileDto
  onClose: () => void
}) {
  const { data } = useGetUpdatePersonalCalendarProfileSuspense({
    profileId: props.profile.webCalendarProfileId!,
  })
  return (
    <EditContent
      {...props}
      initialValues={{
        ...defaultCalendarProfileValues,
        name: data.name ?? "",
        description: data.description ?? "",
        personalSettings:
          data.settings ?? defaultCalendarProfileValues.personalSettings,
        visibleForUserIds: data.visibleForUserIds ?? [],
      }}
    />
  )
}

function EditActivityContent(props: {
  profile: WebCalendarProfileDto
  onClose: () => void
}) {
  const { data } = useGetUpdateActivityCalendarProfileSuspense({
    profileId: props.profile.webCalendarProfileId!,
  })
  return (
    <EditContent
      {...props}
      initialValues={{
        ...defaultCalendarProfileValues,
        provider: "activities",
        name: data.name ?? "",
        description: data.description ?? "",
        activitySettings: data.settings ?? {},
        visibleForUserIds: data.visibleForUserIds ?? [],
      }}
    />
  )
}

export function CreateCalendarProfileModal({
  isShared,
  onClose,
}: ContentProps) {
  const { t } = useTranslation("app_server")
  return (
    <DefaultModal
      opened
      size="lg"
      title={t(
        isShared
          ? "Calendar_Subscriptions_CreateSharedCalendar"
          : "Calendar_Subscriptions_CreatePersonalCalendar"
      )}
      onClose={onClose}
    >
      {isShared ? (
        <CreateContent isShared onClose={onClose} />
      ) : (
        <CreatePersonalContent isShared={false} onClose={onClose} />
      )}
    </DefaultModal>
  )
}

export function EditCalendarProfileModal(props: {
  profile: WebCalendarProfileDto
  onClose: () => void
}) {
  const { t } = useTranslation("app_server")
  return (
    <DefaultModal
      opened
      size="lg"
      title={t(
        props.profile.isShared
          ? "Calendar_Subscriptions_EditSharedCalendar"
          : "Calendar_Subscriptions_EditModalTitle"
      )}
      onClose={props.onClose}
    >
      {props.profile.isActivityCalendar ? (
        <EditActivityContent {...props} />
      ) : (
        <EditPersonalContent {...props} />
      )}
    </DefaultModal>
  )
}
